import { useApolloClient } from "@apollo/client";
import { useAppContext } from "contexts/AppContext";
import React, { useState } from "react";
import {
  Card,
  Col,
  Container,
  Dropdown,
  Form,
  Row,
  Table,
} from "react-bootstrap";
import contactDetails from "gql/dashboard/contacts/contactDetails.gql";
import paymentDocumentsgql from "gql/dashboard/contacts/paymentDocuments.gql";
import { useNavigate } from "react-router-dom";
import DefaultCurrency from "components/DefaultCurrency";
import DateFormat from "components/DateFormat";
import Loading from "components/Loading";
import PaymentDetails from "./PaymentDetails";

const Contacts = () => {
  // contexts
  const { appState } = useAppContext();

  // states
  const [search, setSearch] = useState("");
  const [contactData, setContactData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);

  // hooks
  const navigate = useNavigate();
  const apollo = useApolloClient();

  // functions
  const contactSelected = (contact) => {
    setContactData(contact);
    document.getElementById("contact-information")?.scrollIntoView();
    setLoading(true);
    apollo
      .query({
        query: contactDetails,
        variables: { id: contact.id },
      })
      .then((data) => {
        const contact = data.data.companies[0];
        setPaymentLoading(true);
        apollo
          .query({
            query: paymentDocumentsgql,
            variables: { id: contact.id },
          })
          .then((data) => {
            const docs = data.data.companies[0];
            (docs.invoices || []).forEach((invoice) => {
              if (invoice.balance === 0) {
                invoice.status = <span className="text-success">Paid</span>;
              } else if (new Date(invoice.dueDate) > new Date()) {
                invoice.status = <span className="text-warning">Open</span>;
              } else {
                invoice.status = (
                  <span className="text-danger">Passed Due</span>
                );
              }
            });

            docs.balanceDocuments = [
              ...(docs.invoices || []).map((x) => ({
                ...x,
                type: "Invoice",
              })),
              ...(docs.creditMemos || []).map((x) => ({
                ...x,
                type: "Credit Memo",
              })),
              ...(docs.payments || []).map((x) => ({
                ...x,
                type: "Payment",
              })),
            ];

            docs.balanceDocuments = docs.balanceDocuments.sort((a, b) =>
              a.date > b.date ? -1 : 1
            );
            setContactData((contact) => ({
              ...contact,
              balanceDocuments: docs.balanceDocuments,
            }));
          })
          .catch((e) => { })
          .finally((f) => setPaymentLoading(false));

        contact.balanceDocuments = [];
        setContactData(contact);
      })
      .catch((e) => { })
      .finally((f) => setLoading(false));
  };

  const newDocument = (docCode) => {
    if (docCode === 0) {
      localStorage.removeItem("temp-order");
      navigate("/dashboard/sales/orders/form?customer=" + contactData.id);
    }
  };

  return (
    <React.Fragment>
      {selectedDoc && (
        <PaymentDetails doc={selectedDoc} setDoc={setSelectedDoc} />
      )}
      <Container fluid className="p-0">
        <Row>
          <Col md={4} className="h-80vh overflow-auto">
            <Container
              className="py-1 bg-white shadow-sm position-sticky top-0"
              fluid
            >
              <Form.Control
                placeholder="Search..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </Container>
            <Table responsive striped hover>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Name</th>
                </tr>
              </thead>
              <tbody>
                {appState.resources.customers
                  .filter((c) =>
                    c.name.toLowerCase().includes(search.toLowerCase())
                  )
                  .map((company, index) => (
                    <tr key={company.id}>
                      <td>{index + 1}</td>
                      <td>
                        <span
                          className="link-primary cursor-pointer"
                          onClick={() => contactSelected(company)}
                        >
                          {company.name}
                        </span>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </Table>
          </Col>
          <Col id="contact-information" md={8}>
            {contactData && (
              <Card className="mt-3 h-100">
                <Card.Header>
                  <Container
                    fluid
                    className="d-flex justify-content-between p-0"
                  >
                    <span>
                      <Card.Title>{contactData.name}</Card.Title>
                      <Card.Subtitle>{contactData.emails}</Card.Subtitle>
                    </span>
                    <span>
                      {contactData.customer && (
                        <Dropdown>
                          <Dropdown.Toggle variant="success">
                            New
                          </Dropdown.Toggle>
                          <Dropdown.Menu>
                            <Dropdown.Item onClick={() => newDocument(0)}>
                              New Order
                            </Dropdown.Item>
                          </Dropdown.Menu>
                        </Dropdown>
                      )}
                    </span>
                  </Container>
                </Card.Header>
                <Card.Body>
                  {loading && <Loading />}
                  {!loading && contactData && (
                    <React.Fragment>
                      <Row className="fw-bold">
                        <Col sm={3}>
                          <span>Name:</span>
                        </Col>
                        <Col sm={9}>
                          <span>{contactData.name}</span>
                        </Col>
                      </Row>
                      <Row className="fw-bold">
                        <Col sm={3}>
                          <span>Email:</span>
                        </Col>
                        <Col sm={9}>
                          <span>{contactData.emails}</span>
                        </Col>
                      </Row>
                      <Row className="fw-bold">
                        <Col sm={3}>
                          <span>Address:</span>
                        </Col>
                        <Col sm={9}>
                          <span>{contactData.billingAddress?.address}</span>
                        </Col>
                      </Row>
                      <Row className="fw-bold">
                        <Col sm={3}>
                          <span>Payment Terms:</span>
                        </Col>
                        <Col sm={9}>
                          <span>
                            {contactData.companyFinancials?.paymentTerms?.name}
                          </span>
                        </Col>
                      </Row>
                      <Row className="fw-bold">
                        <Col sm={3}>
                          <span>Open Balance:</span>
                        </Col>
                        <Col sm={9} className="text-danger h5">
                          <DefaultCurrency
                            value={contactData.accountingOutstandingBalance}
                          />
                        </Col>
                      </Row>
                      <Row className="mt-5">
                        <Col sm={12}>
                          <span>
                            <b>Payment Records:</b>
                          </span>
                        </Col>
                        <Col sm={12} className="h-40vh overflow-auto">
                          <Table striped hover bordered>
                            <thead className="position-sticky top-0 bg-white">
                              <tr>
                                <th>#</th>
                                <th>Doc Type</th>
                                <th>Doc Number</th>
                                <th>Doc Date</th>
                                <th>Due Date</th>
                                <th>Status</th>
                                <th>Amount</th>
                                <th>Balance</th>
                              </tr>
                            </thead>
                            <tbody>
                              {!paymentLoading &&
                                contactData.balanceDocuments?.map(
                                  (doc, index) => (
                                    <tr key={index}>
                                      <td>{index + 1}</td>
                                      <td>{doc.type}</td>
                                      <td>
                                        <span
                                          className="link-primary cursor-pointer"
                                          onClick={() => setSelectedDoc(doc)}
                                        >
                                          {doc.docNumber}
                                        </span>
                                      </td>
                                      <td>
                                        <DateFormat value={doc.date} />
                                      </td>
                                      <td>
                                        <DateFormat value={doc.dueDate} />
                                      </td>
                                      <td>{doc.status}</td>
                                      <td>
                                        <DefaultCurrency value={doc.amount} />
                                      </td>
                                      <td>
                                        <DefaultCurrency value={doc.balance} />
                                      </td>
                                    </tr>
                                  )
                                )}
                            </tbody>
                          </Table>
                          {paymentLoading && <Loading />}
                        </Col>
                      </Row>
                    </React.Fragment>
                  )}
                </Card.Body>
              </Card>
            )}
          </Col>
        </Row>
      </Container>
    </React.Fragment>
  );
};

export default Contacts;
